import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
} from "react-router-dom";
import FloatingPanel from './components/FloatingPanel';
import GameRoute from './routes/GamePage';
import Home from './routes/Home';

import { initContext } from './context/context';
import { RTMTWS, ConnectionState, AuthUser } from '@mancala/core';
import { Theme } from '@mancala/core';
import LobyPage from './routes/LobyPage';
import PrivacyPage from './routes/PrivacyPage';
import RulesPage from './routes/RulesPage';
import LearnPage from './routes/LearnPage';
import LessonPage from './routes/LessonPage';
import swal from 'sweetalert';
import Util from './util/Util';
import { server } from './const/config';
import { useTranslation } from 'react-i18next';
import { applyThemeClass } from './theme/themes';
import { AuthUserContext } from './context/AuthUserContext';
import './styles/app.css';

const context = initContext();

const MancalaApp: FunctionComponent = () => {

    const { t, i18n } = useTranslation();

    // Set once the server has authenticated the connection.
    const [authUser, setAuthUser] = useState<AuthUser | undefined>(undefined);

    // Pages compare this with the game's player ids.
    const userKey = authUser?.id;

    const [hasSession, setHasSession] = useState<boolean>(false);

    const [connectionState, setConnetionState] = useState<ConnectionState>("connecting");

    const [theme, setTheme] = useState<Theme>(context.themeManager.theme);

    const onConnectionError = (event: Event) => console.error("(RTMT) Connection Error: ", event);

    const onConnectionChange = (_connectionState: ConnectionState) => setConnetionState(_connectionState);

    const onThemeChange = (theme: Theme) => {
        applyThemeClass(theme);
        setTheme(theme);
    };

    React.useEffect(() => {
        //@ts-ignore
        const deviceLanguage = navigator.language || navigator.userLanguage;
        i18n.changeLanguage(deviceLanguage === "tr-TR" ? "tr" : "en")
      }, []);

    const onAuthenticated = (user: AuthUser) => setAuthUser(user);

    // If the session can no longer be refreshed, start over as a new guest.
    const getAccessTokenOrLoginAgain = async () => {
        try {
            return await context.authService.getAccessToken();
        } catch (error) {
            context.authService.loginAsGuest();
            throw error;
        }
    }

    const connectRTMT = () => {
        const rtmt = context.rtmt as RTMTWS;
        rtmt.on("error", onConnectionError);
        rtmt.on("connectionchange", onConnectionChange);
        rtmt.on("authenticated", onAuthenticated);
        rtmt.connectWebSocket(server.wsServerAdress, getAccessTokenOrLoginAgain);
        return rtmt;
    }

    const startSession = async () => {
        const authService = context.authService;
        if (authService.isCallback()) {
            let returnTo = "/";
            try {
                returnTo = await authService.handleCallback();
            } catch (error) {
                // e.g. the callback page was reloaded; fall back to the stored session.
                console.error("(Auth) Login callback failed", error);
            }
            window.history.replaceState(null, "", returnTo);
        }
        const user = await authService.getUser();
        if (!user) {
            await authService.loginAsGuest();
            return;
        }
        setHasSession(true);
        connectRTMT();
    }

    const disposeApp = () => {
        context.rtmt?.dispose();
        context.rtmt.off("authenticated", onAuthenticated);
        context.themeManager.off("themechange", onThemeChange);
    }

    React.useEffect(() => {
        applyThemeClass(context.themeManager.theme);
        startSession().catch((error) => {
            swal(t("Error") + "!", t("ErrorWhenRetrievingInformation"), "error");
            console.error(error);
        });
        const removeUserLoadedListener = context.authService.onUserLoaded(
            (user) => (context.rtmt as RTMTWS).reauthenticate(user.access_token));
        context.themeManager.on("themechange", onThemeChange);
        return () => {
            removeUserLoadedListener();
            disposeApp();
        };
    }, []);

    return (
        <>
            {hasSession && <AuthUserContext.Provider value={authUser}><BrowserRouter>
                <Routes>
                    <Route index element={<Home context={context} theme={theme} userKey={userKey} authUser={authUser} />} />
                    <Route path="/" >
                        <Route path="game" >
                            <Route path=":gameId" element={<GameRoute context={context} theme={theme} userKey={userKey} />}  ></Route>
                        </Route>
                        <Route path="loby" element={<LobyPage context={context} theme={theme} userKey={userKey} />}>
                        </Route>
                        <Route path="privacy" element={<PrivacyPage context={context} theme={theme} userKey={userKey} />}>
                        </Route>
                        <Route path="rules" element={<RulesPage context={context} theme={theme} />} />
                        <Route path="learn" element={<LearnPage context={context} theme={theme} />} />
                        <Route path="learn/:ruleId" element={<LessonPage context={context} theme={theme} />} />
                    </Route>
                </Routes>
            </BrowserRouter></AuthUserContext.Provider>}
            <FloatingPanel visible={connectionState != "connected"}>
                {Util.getTextByConnectionState(context, connectionState, t)}
            </FloatingPanel>
        </>
    );
}

export default MancalaApp;